/* eslint-disable no-unused-vars */
/* eslint-disable no-underscore-dangle */
import React, { Ref, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
  AutoComplete,
  Card,
  Flex,
  Button,
  Modal,
  Select,
  Skeleton,
  message,
} from 'antd';
import { CloseCircleFilled, WhatsAppOutlined } from '@ant-design/icons';
import { getById } from 'services/DriverService';
import {
  kindRules,
  Driver as DriverType,
  JourneyState,
} from 'interfaces/journey';
import { useJourneyDashboardContext } from '../context';
import {
  getDriverVehiclePlates,
  useAssignDriver,
  useUnassign,
} from 'services/journeys';
import useCollectionsEnabled from 'hooks/useCollectionsEnabled';

interface DriverProps {
  disabled?: boolean;
  height?: number;
}

const Driver = React.forwardRef<HTMLDivElement, DriverProps>(
  ({ disabled, height }, ref) => {
    const { id, driver, drivers, state, kind, vehiclePlate } =
      useJourneyDashboardContext();
    const collectionsEnabled = useCollectionsEnabled();
    const asksPlate = collectionsEnabled && kindRules(kind).collections;
    const [plateModalOpen, setPlateModalOpen] = useState(false);
    const [plate, setPlate] = useState('');
    const [lastPlates, setLastPlates] = useState<string[]>([]);
    const [chosenDriver, setChosenDriver] = useState<DriverType | undefined>();
    const { mutate: assignDriver, isPending: isPendingAssign } =
      useAssignDriver();
    const { mutate: unassignDriver, isPending: isPendingUnassign } =
      useUnassign();

    const isPending = isPendingAssign || isPendingUnassign;

    useEffect(() => {
      if (driver) {
        setChosenDriver(driver);
      } else {
        setChosenDriver(undefined);
      }
    }, [driver, state]);

    const driverOptions = useMemo(
      () =>
        drivers
          ?.filter((d) => d.active)
          .map((d) => ({
            label: `${d.name} ${d.lastName}`,
            value: d.id,
          })) ?? [],
      [drivers],
    );

    const handleSelectDriver = async (value: string | number) => {
      const newDriver = await getById(value);
      setChosenDriver({
        ...newDriver,
        fullname: `${newDriver.name} ${newDriver.lastName}`,
      });
    };

    const suggestedDriver = Number(
      new URLSearchParams(useLocation().search).get('suggestedDriver'),
    );
    const suggestionApplied = useRef(false);
    const [suggested, setSuggested] = useState(false);
    useEffect(() => {
      if (
        suggestionApplied.current ||
        !suggestedDriver ||
        state !== JourneyState.Draft ||
        driver?.id
      )
        return;
      suggestionApplied.current = true;
      handleSelectDriver(suggestedDriver)
        .then(() => setSuggested(true))
        .catch(() => undefined);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestedDriver, state, driver?.id]);

    const handleAssign = async () => {
      if (!id || !chosenDriver || !chosenDriver.id) return;
      if (asksPlate) {
        const plates = await getDriverVehiclePlates(chosenDriver.id).catch(
          () => [],
        );
        setLastPlates(plates);
        setPlate(plates[0] ?? '');
        setPlateModalOpen(true);
        return;
      }
      try {
        assignDriver({ journeyId: id, driverId: chosenDriver.id });
      } catch (error) {
        console.error(error);
        message.error('Ocurrió un error al asignar al driver');
      }
    };

    const confirmPlateAndAssign = () => {
      if (!id || !chosenDriver?.id || !plate.trim()) return;
      setPlateModalOpen(false);
      assignDriver({
        journeyId: id,
        driverId: chosenDriver.id,
        vehiclePlate: plate.trim(),
      });
    };

    const handleUnassign = () => {
      if (!id || !driver || !driver.id) return;
      try {
        unassignDriver({ journeyId: id, driverId: driver.id });
      } catch (error) {
        console.error(error);
        message.error('Ocurrió un error al desasignar al driver');
      }
    };

    return (
      <Card
        style={{
          height: height || '100%',
          borderStyle: chosenDriver ? 'solid' : 'dashed',
          overflow: 'hidden',
        }}
        bodyStyle={{
          padding: 13,
          height: height || '100%',
        }}
        ref={ref as Ref<HTMLDivElement>}
      >
        <Flex
          vertical
          justify="space-between"
          align="center"
          style={{ height: '100%', width: '100%' }}
        >
          {state !== JourneyState.Accepted &&
            state !== JourneyState.Started &&
            state !== JourneyState.Finished && (
              <Select
                placeholder="Seleccioná un driver"
                style={{ width: '100%' }}
                options={driverOptions}
                allowClear
                showSearch
                filterOption={(input: string, option: any) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                clearIcon={
                  <CloseCircleFilled style={{ color: 'red', fontSize: 14 }} />
                }
                onSelect={handleSelectDriver}
                onClear={() => setChosenDriver(undefined)}
                value={
                  chosenDriver && chosenDriver.id ? chosenDriver.id : undefined
                }
                disabled={disabled}
              />
            )}
          {chosenDriver?.fullname && chosenDriver?.email ? (
            <Flex
              vertical
              justify="start"
              style={{
                width: '100%',
                marginTop:
                  state !== JourneyState.Accepted &&
                  state !== JourneyState.Started &&
                  state !== JourneyState.Finished
                    ? 0
                    : 52,
              }}
            >
              <b
                style={{
                  fontSize: 20,
                }}
              >
                {chosenDriver.fullname}
              </b>
              <p
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={chosenDriver.email}
              >
                {chosenDriver.email}
              </p>
              {asksPlate && vehiclePlate && driver?.id === chosenDriver.id && (
                <p style={{ margin: 0 }}>Patente {vehiclePlate}</p>
              )}
              {suggested &&
                !driver?.id &&
                chosenDriver.id === suggestedDriver && (
                  <p style={{ margin: 0, color: '#8c8c8c' }}>
                    Sugerido por la plantilla: falta asignarlo
                  </p>
                )}
            </Flex>
          ) : (
            <Skeleton
              title={false}
              paragraph={{ rows: 2 }}
              active={!!chosenDriver}
              style={{
                marginTop:
                  state !== JourneyState.Accepted &&
                  state !== JourneyState.Started &&
                  state !== JourneyState.Finished
                    ? 0
                    : 52,
              }}
            />
          )}
          <Flex justify={'end'} gap={16} style={{ width: '100%' }}>
            <Button
              title="Contactar"
              type="text"
              style={{
                fontSize: 18,
                color: chosenDriver ? 'green' : 'lightgrey',
              }}
              icon={<WhatsAppOutlined />}
              disabled={!chosenDriver}
              href={
                chosenDriver && chosenDriver.city
                  ? `https://web.whatsapp.com/send?phone=${chosenDriver.city.dialingCode}${chosenDriver.mobile}`
                  : ''
              }
              target="_blank"
            >
              Contactar
            </Button>
            {state !== JourneyState.Accepted &&
              state !== JourneyState.Started &&
              state !== JourneyState.Finished && (
                <Button
                  type="primary"
                  disabled={
                    isPending ||
                    disabled ||
                    (!chosenDriver && !driver) ||
                    chosenDriver?.id === driver?.id
                  }
                  danger={driver && !chosenDriver}
                  loading={isPending}
                  onClick={() =>
                    driver && !chosenDriver ? handleUnassign() : handleAssign()
                  }
                >
                  {driver && !chosenDriver ? 'Desasignar' : 'Asignar'}
                </Button>
              )}
          </Flex>
        </Flex>
        <Modal
          open={plateModalOpen}
          title="Patente del vehículo"
          okText="Asignar"
          cancelText="Cancelar"
          okButtonProps={{ disabled: !plate.trim() }}
          onOk={confirmPlateAndAssign}
          onCancel={() => setPlateModalOpen(false)}
          destroyOnClose
        >
          <p>
            {lastPlates.length > 0
              ? `La última patente de ${chosenDriver?.fullname ?? 'este driver'} es ${lastPlates[0]}. Confirmala o cambiala por la del vehículo con el que sale.`
              : `${chosenDriver?.fullname ?? 'Este driver'} no tiene una patente guardada. Cargá la del vehículo con el que sale.`}
          </p>
          <AutoComplete
            autoFocus
            size="large"
            value={plate}
            placeholder="AA123BB"
            options={lastPlates.map((value) => ({ value }))}
            style={{ width: '100%', textTransform: 'uppercase' }}
            onChange={setPlate}
          />
        </Modal>
      </Card>
    );
  },
);

export default Driver;
